/**
 * BESTANDTEILE FESTLEGEN — Editor fuer die Bestandteile einer Paketposition
 * (Plan docs/positionsbestandteile-plan.md, Stufe B4; Oberflaechenwort
 * „Bestandteile", E10).
 *
 * Eingebettet in: Katalog (Vorlage je Katalogposition), Projektanlage,
 * Nachtrag, Angebot (intern) und den Positionsdialog im Projekt (Paket D).
 * Deshalb kompakt, ohne eigenen Rahmen nach aussen und in schmalen Spalten
 * (Seitenleiste des Katalogs, Handy) bedienbar: jede Zeile bricht um.
 *
 * Bewusst KEINE Preise und keine Mengen — hier wird nur festgelegt, WAS
 * erledigt sein muss. Wer festlegen darf (E6: nur Admin), entscheidet der
 * Aufrufer ueber `disabled`; gesperrt zeigt der Editor die Liste nur lesend.
 *
 * Alle Aenderungen an der Liste laufen ueber die reinen Funktionen in
 * bestandteileEditorLogic.ts (getestet); hier steht nur die Bedienung.
 */
import React, { useMemo, useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, ListChecks, Plus, Trash2 } from 'lucide-react';
import type { BestandteilQuelle, PositionsBestandteil, ServiceItem } from '../../types';
import {
    BESTANDTEIL_AB_VORGABE,
    BESTANDTEIL_QUELLEN,
    BESTANDTEIL_QUELLE_LABEL,
    effektivesAb,
    hatBestandteile,
    istModulQuelle,
    type AusgabeAnlass,
} from '../../services/bestandteile/bestandteilLogic';
import {
    alsBestandteilWert,
    benenneBestandteilUm,
    editierbareListe,
    entferneBestandteil,
    fuegeBestandteilHinzu,
    pruefeBestandteilListe,
    setzeBestandteilAb,
    uebernimmVorlage,
    verschiebeBestandteil,
    vorgabeLabel,
    wechsleBestandteilQuelle,
} from './bestandteileEditorLogic';

export interface BestandteileEditorProps {
    wert: PositionsBestandteil[] | null | undefined;
    /** Leere Liste → null. */
    onChange: (neu: PositionsBestandteil[] | null) => void;
    /** Alle je benutzten Keys (aktuelle, frueher entfernte, Keys aus Eintraegen) — neue Keys kollidieren nie. */
    belegteKeys?: readonly string[];
    /** Keys mit aktiven Eintraegen — Entfernen nur mit Rueckfrage („… ist bereits erledigt. Trotzdem entfernen?"). */
    keysMitEintrag?: readonly string[];
    /**
     * `keysMitEintrag` ist nur VERMUTET (bekannt ist allein, DASS es erledigte
     * Bestandteile gibt, nicht welche — z. B. im Nachtrag, der nur
     * `bestandteileOffen` kennt). Die Rueckfrage sagt dann „möglicherweise".
     */
    keysMitEintragVermutet?: boolean;
    /** Katalogvorlage — Knopf „Aus Katalog übernehmen" (ersetzt nur bei leerer Liste, sonst Rueckfrage). */
    vorlage?: PositionsBestandteil[] | null;
    disabled?: boolean;
    /** Zeigt Fehler aus pruefeDefinition, falls uebergeben. */
    service?: ServiceItem;
}

type Rueckfrage = { art: 'entfernen'; key: string } | { art: 'vorlage' } | null;

const ANLASS_LABEL: Record<AusgabeAnlass, string> = { freigabe: 'Freigabe', versand: 'Versand' };

const feldKlasse =
    'min-w-0 px-2 py-1.5 bg-white border border-slate-200 rounded-md text-xs text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/15 disabled:opacity-60';
const iconKnopf =
    'p-2 sm:p-1.5 rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400';

/** „erledigt ab Versand" — fuer die Lese-Ansicht. */
const abText = (b: PositionsBestandteil): string => {
    const ab = effektivesAb(b);
    return ab ? `erledigt ab ${ANLASS_LABEL[ab]}` : 'von Hand';
};

export const BestandteileEditor: React.FC<BestandteileEditorProps> = ({
    wert,
    onChange,
    belegteKeys = [],
    keysMitEintrag = [],
    keysMitEintragVermutet = false,
    vorlage,
    disabled = false,
    service,
}) => {
    // Roh, NICHT normalisiert: beim Tippen ist ein Label kurz leer, und die
    // Normalisierung wuerfe die Zeile dann weg. Gespeichert wird ohnehin nur,
    // was pruefeDefinition durchlaesst (Aufrufer sperren bei Fehlern).
    const liste = useMemo(() => editierbareListe(wert), [wert]);
    const [neuQuelle, setNeuQuelle] = useState<BestandteilQuelle>('vorankuendigung');
    const [neuLabel, setNeuLabel] = useState(() => vorgabeLabel('vorankuendigung'));
    // Hat der Bediener das Label selbst getippt? Dann ueberschreibt ein
    // Quellenwechsel es nicht mehr.
    const [neuLabelEigen, setNeuLabelEigen] = useState(false);
    const [rueckfrage, setRueckfrage] = useState<Rueckfrage>(null);

    const fehler = useMemo(() => (liste.length > 0 ? pruefeBestandteilListe(liste, service) : null), [liste, service]);
    const mitEintrag = useMemo(() => new Set(keysMitEintrag || []), [keysMitEintrag]);
    const vorlageDa = hatBestandteile({ bestandteile: vorlage ?? null });

    const melde = (neu: PositionsBestandteil[]) => onChange(alsBestandteilWert(neu));

    const hinzufuegen = () => {
        const label = neuLabel.trim();
        if (!label) return;
        melde(fuegeBestandteilHinzu(liste, { label, quelle: neuQuelle }, belegteKeys));
        setNeuLabel(vorgabeLabel(neuQuelle));
        setNeuLabelEigen(false);
    };

    const wechsleNeuQuelle = (q: BestandteilQuelle) => {
        setNeuQuelle(q);
        if (!neuLabelEigen) setNeuLabel(vorgabeLabel(q));
    };

    const entfernen = (key: string) => {
        if (mitEintrag.has(key)) {
            setRueckfrage({ art: 'entfernen', key });
            return;
        }
        melde(entferneBestandteil(liste, key));
    };

    const vorlageAnwenden = () => {
        melde(uebernimmVorlage(liste, vorlage, belegteKeys, keysMitEintrag).liste);
        setRueckfrage(null);
    };

    const vorlageKlick = () => {
        if (liste.length === 0) vorlageAnwenden();
        else setRueckfrage({ art: 'vorlage' });
    };

    // ── Lese-Ansicht (kein Recht zum Festlegen, gesperrtes Angebot) ─────────
    if (disabled) {
        return (
            <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <ListChecks className="w-3.5 h-3.5" /> Bestandteile{liste.length > 0 ? ` (${liste.length})` : ''}
                </div>
                {liste.length === 0 ? (
                    <p className="text-[11px] text-slate-400">Keine Bestandteile.</p>
                ) : (
                    <ol className="space-y-1">
                        {liste.map((b, i) => (
                            <li key={b.key || i} className="flex flex-wrap items-baseline gap-x-2 text-xs text-slate-700">
                                <span className="w-4 text-right text-[11px] tabular-nums text-slate-400">{i + 1}.</span>
                                <span className="font-semibold">{b.label}</span>
                                <span className="text-[11px] text-slate-500">
                                    {BESTANDTEIL_QUELLE_LABEL[b.quelle] ?? b.quelle} · {abText(b)}
                                </span>
                            </li>
                        ))}
                    </ol>
                )}
                {fehler && <p className="text-[11px] font-semibold text-red-700">{fehler}</p>}
            </div>
        );
    }

    const rueckfrageEintrag = rueckfrage?.art === 'entfernen' ? liste.find((b) => b.key === rueckfrage.key) : undefined;
    const vorlageErgebnis = rueckfrage?.art === 'vorlage' ? uebernimmVorlage(liste, vorlage, belegteKeys, keysMitEintrag) : null;

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <ListChecks className="w-3.5 h-3.5" /> Bestandteile{liste.length > 0 ? ` (${liste.length})` : ''}
                </div>
                {vorlageDa && (
                    <button
                        type="button"
                        onClick={vorlageKlick}
                        className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 px-1 py-1"
                        title="Bestandteile aus der Katalogvorlage dieser Position übernehmen"
                    >
                        Aus Katalog übernehmen
                    </button>
                )}
            </div>
            <p className="text-[11px] leading-snug text-slate-500">
                Die Position wird erst abrechenbar, wenn alle Bestandteile erledigt sind. Nur intern — erscheint auf keinem Beleg.
            </p>

            {rueckfrage?.art === 'vorlage' && vorlageErgebnis && (
                <div className="flex flex-wrap items-center gap-2 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span className="min-w-0 flex-1">
                        Die Liste ({liste.length}) wird durch die Katalogvorlage ({vorlageErgebnis.liste.length}) ersetzt.
                        {vorlageErgebnis.erledigtEntfernt.length > 0 && (
                            <>
                                {' '}
                                {keysMitEintragVermutet ? 'Möglicherweise bereits erledigt' : 'Bereits erledigt'} und danach nicht mehr in der Liste:{' '}
                                {vorlageErgebnis.erledigtEntfernt
                                    .map((k) => `„${liste.find((b) => b.key === k)?.label ?? k}“`)
                                    .join(', ')}
                                .
                            </>
                        )}
                    </span>
                    <button type="button" onClick={vorlageAnwenden} className="font-semibold text-amber-900 underline underline-offset-2 px-1 py-1">
                        Ersetzen
                    </button>
                    <button type="button" onClick={() => setRueckfrage(null)} className="text-amber-800 px-1 py-1">
                        Abbrechen
                    </button>
                </div>
            )}

            {liste.length > 0 && (
                <ol className="space-y-1.5">
                    {liste.map((b, i) => {
                        const modul = istModulQuelle(b.quelle);
                        const vorgabeAb = modul ? BESTANDTEIL_AB_VORGABE[b.quelle as Exclude<BestandteilQuelle, 'hand'>] : null;
                        return (
                            <li key={b.key || i} className="rounded-md border border-slate-200 bg-white px-2 py-1.5">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="w-4 text-right text-[11px] tabular-nums text-slate-400">{i + 1}.</span>
                                    <input
                                        value={b.label ?? ''}
                                        onChange={(e) => melde(benenneBestandteilUm(liste, b.key, e.target.value))}
                                        // Leerzeichen am Rand erst beim Verlassen weg —
                                        // beim Tippen gehoeren sie noch zum Wort.
                                        onBlur={() => {
                                            const getrimmt = String(b.label ?? '').trim();
                                            if (getrimmt !== b.label) melde(benenneBestandteilUm(liste, b.key, getrimmt));
                                        }}
                                        className={`${feldKlasse} flex-1 basis-40`}
                                        placeholder="Bezeichnung"
                                        aria-label={`Bezeichnung Bestandteil ${i + 1}`}
                                    />
                                    <select
                                        value={b.quelle}
                                        onChange={(e) => melde(wechsleBestandteilQuelle(liste, b.key, e.target.value as BestandteilQuelle))}
                                        className={`${feldKlasse} basis-36 grow sm:grow-0`}
                                        aria-label={`Quelle Bestandteil ${i + 1}`}
                                        title="Woher der Bestandteil seine Erledigung bekommt"
                                    >
                                        {BESTANDTEIL_QUELLEN.map((q) => (
                                            <option key={q} value={q}>{BESTANDTEIL_QUELLE_LABEL[q]}</option>
                                        ))}
                                    </select>
                                    {modul ? (
                                        <select
                                            value={effektivesAb(b) ?? vorgabeAb ?? 'freigabe'}
                                            onChange={(e) => melde(setzeBestandteilAb(liste, b.key, e.target.value as AusgabeAnlass))}
                                            className={`${feldKlasse} basis-32 grow sm:grow-0`}
                                            aria-label={`Erledigt ab, Bestandteil ${i + 1}`}
                                            title="Ab welcher Ausgabe im Modul der Bestandteil als erledigt gilt"
                                        >
                                            {(['freigabe', 'versand'] as const).map((a) => (
                                                <option key={a} value={a}>
                                                    ab {ANLASS_LABEL[a]}{a === vorgabeAb ? ' (Vorgabe)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="basis-32 grow sm:grow-0 px-1 text-[11px] text-slate-400">erledigt von Hand</span>
                                    )}
                                    <div className="ml-auto flex items-center">
                                        <button
                                            type="button"
                                            onClick={() => melde(verschiebeBestandteil(liste, b.key, -1))}
                                            disabled={i === 0}
                                            className={iconKnopf}
                                            title="Nach oben"
                                            aria-label={`Bestandteil ${i + 1} nach oben`}
                                        >
                                            <ArrowUp className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => melde(verschiebeBestandteil(liste, b.key, 1))}
                                            disabled={i === liste.length - 1}
                                            className={iconKnopf}
                                            title="Nach unten"
                                            aria-label={`Bestandteil ${i + 1} nach unten`}
                                        >
                                            <ArrowDown className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => entfernen(b.key)}
                                            className={`${iconKnopf} hover:bg-red-50 hover:text-red-600`}
                                            title="Bestandteil entfernen"
                                            aria-label={`Bestandteil ${i + 1} entfernen`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                                {rueckfrageEintrag && rueckfrageEintrag.key === b.key && (
                                    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                        <span className="min-w-0 flex-1">
                                            „{b.label || b.key}“ ist {keysMitEintragVermutet ? 'möglicherweise ' : ''}bereits erledigt. Trotzdem entfernen?
                                            Der Eintrag bleibt im Verlauf, zählt aber nicht mehr.
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => { melde(entferneBestandteil(liste, b.key)); setRueckfrage(null); }}
                                            className="font-semibold text-red-700 underline underline-offset-2 px-1 py-1"
                                        >
                                            Entfernen
                                        </button>
                                        <button type="button" onClick={() => setRueckfrage(null)} className="text-amber-800 px-1 py-1">
                                            Abbrechen
                                        </button>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ol>
            )}

            {fehler && <p className="text-[11px] font-semibold text-red-700">{fehler}</p>}

            {/* Neuer Bestandteil: Quelle waehlen belegt das Label vor, der
                Key entsteht beim Hinzufuegen einmal aus dem Label. */}
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-dashed border-slate-300 px-2 py-1.5">
                <select
                    value={neuQuelle}
                    onChange={(e) => wechsleNeuQuelle(e.target.value as BestandteilQuelle)}
                    className={`${feldKlasse} basis-36 grow sm:grow-0`}
                    aria-label="Quelle des neuen Bestandteils"
                >
                    {BESTANDTEIL_QUELLEN.map((q) => (
                        <option key={q} value={q}>{BESTANDTEIL_QUELLE_LABEL[q]}</option>
                    ))}
                </select>
                <input
                    value={neuLabel}
                    onChange={(e) => { setNeuLabel(e.target.value); setNeuLabelEigen(e.target.value.trim() !== ''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); hinzufuegen(); } }}
                    className={`${feldKlasse} flex-1 basis-40`}
                    placeholder={neuQuelle === 'hand' ? 'z. B. Abstimmung Bauherr' : 'Bezeichnung'}
                    aria-label="Bezeichnung des neuen Bestandteils"
                />
                <button
                    type="button"
                    onClick={hinzufuegen}
                    disabled={!neuLabel.trim()}
                    className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                    <Plus className="w-3.5 h-3.5" /> Bestandteil hinzufügen
                </button>
            </div>
        </div>
    );
};
